import { computed, Injectable, signal } from '@angular/core';
import { Agency, AgencyKeys, Agent, AGENT_STATUS, ApproveDenyReasonVisibilityLevel } from '@ag-common-lib/public-api';
import { AgencyService } from 'ag-common-svc/public-api';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { uniqueId } from 'lodash';
import { ModalWindowService } from '../../../components/modal-window/modal-window.service';
import { ModalWindowUserAction } from '../../../components/modal-window/modal-window.model';
import {
  AgencyHierarchyManagerMode,
  MgaHierarchy,
  TransferConfig,
  TransferTeamResultData,
} from 'ag-common-svc/lib/modules/agency-hierarchy-administration/agency-hierarchy-management/agency-hierarchy-management.model';
import { alert } from 'devextreme/ui/dialog';
import { CloudFunctionsService } from 'ag-common-svc/lib/services/cloud-functions.service';
import { AgencyHierarchyManagementService } from './agency-hierarchy-management.service';

export interface ChangeAgentStatusConfig {
  reasonRequired: boolean;
  title: string;
  actionTitle: string;
  agentStatus: AGENT_STATUS;
  approveDenyReasonVisibilityLevel: ApproveDenyReasonVisibilityLevel;
}

@Injectable()
export class AgentsTransferModalService {
  private readonly _inProgress$ = new BehaviorSubject<boolean>(false);
  inProgress$ = this._inProgress$.asObservable();

  protected agenciesSig = toSignal(this.agencyService.agencies$);
  protected agenciesMapByAgencyIdSig = toSignal(this.agencyService.agenciesMapByAgencyId$);

  promptSettingsSig = signal<{
    teamSize: number;
    teamListUrl: string;
  }>(null);
  agentTransferConfigSig = signal<TransferConfig>(null);

  readonly transferPromptModalId = uniqueId('transferPromptModalId');
  readonly transferModalId = uniqueId('transferModalId');

  constructor(
    private agencyService: AgencyService,
    private modalWindowService: ModalWindowService,
    private cloudFunctionsService: CloudFunctionsService,
    private agencyHierarchyManagementService: AgencyHierarchyManagementService,
  ) {}

  transferTeam = async ({
    mode,
    workSheetName,
    transferedAgent,
    transferedAgency,
    originMgaId,
    targetMgaId,
    defaultHierarchyChanges,
  }: {
    mode: AgencyHierarchyManagerMode;
    workSheetName: string;
    transferedAgent?: Agent;
    transferedAgency?: Agency;
    originMgaId: string;
    targetMgaId?: string;
    defaultHierarchyChanges?: Agent[][];
  }): Promise<{ type: ModalWindowUserAction; data?: TransferTeamResultData }> => {
    const agencies = this.agenciesSig();

    const transferConfig: TransferConfig = {
      originMga: null,
      targetMga: null,
      transferedAgent,
      transferedAgency,
      defaultHierarchyChanges,
    };
    agencies?.forEach(agency => {
      if (agency?.[AgencyKeys.agencyId] === originMgaId) {
        transferConfig.originMga = agency;
      }

      if (!targetMgaId) {
        return;
      }

      if (agency?.[AgencyKeys.agencyId] === targetMgaId) {
        transferConfig.targetMga = agency;
      }
    });
    this.agencyHierarchyManagementService.setMode(mode);
    this.agencyHierarchyManagementService.setMode(mode);
    this.agencyHierarchyManagementService.setTransferConfig(transferConfig);

    const teamSheet = await this.agencyHierarchyManagementService.getTeamListBlob(workSheetName);
    const teamListUrl = teamSheet?.url;
    const teamSize = teamSheet?.teamSize;

    if (!teamSize) {
      return { data: { hierarchyChanges: {} }, type: ModalWindowUserAction.apply };
    }

    this.promptSettingsSig.set({
      teamSize,
      teamListUrl,
    });

    const notificationResult = await firstValueFrom(this.modalWindowService.prompt(this.transferPromptModalId));

    notificationResult?.modalWindow?.forceCloseModal();
    if (!notificationResult || notificationResult?.type === ModalWindowUserAction.abort) {
      return { type: ModalWindowUserAction.abort };
    }

    const transferResults = await firstValueFrom(
      this.modalWindowService.prompt(this.transferModalId).pipe(
        filter(userActionPayload => {
          if (userActionPayload?.type !== ModalWindowUserAction.apply) {
            return true;
          }

          if (this.agencyHierarchyManagementService.transferResolvedSig()) {
            return true;
          }

          alert('Not all Agents transfered', 'Warning').then();

          return false;
        }),
      ),
    );

    if (!transferResults || transferResults?.type === ModalWindowUserAction.abort) {
      return { type: ModalWindowUserAction.abort };
    }
    this.startProgress();

    const hierarchyChanges = this.agencyHierarchyManagementService.hierarchyChangesMapSig();
    const targetMgaLeadSelection = this.agencyHierarchyManagementService.targetMgaLeadSelectionSig();

    this.agencyHierarchyManagementService.logHierarchyChanges({
      workSheetName,
      originMgaId,
      targetMgaId,
      teamBlob: teamSheet?.blob,
    });

    this.stopProgress();

    return { data: { hierarchyChanges, targetMgaLeadSelection }, type: ModalWindowUserAction.apply };
  };

  protected startProgress() {
    this._inProgress$.next(true);
  }

  protected stopProgress() {
    this._inProgress$.next(false);
  }
}
